import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import { categoryHeroImages } from '@/mocks/categories';
import { siteInfo } from '@/mocks/site';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading text-2xl text-foreground-900">分类未找到</h2>
          <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 text-sm">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const articles = getArticlesByCategory(category.slug);
  const heroImage = categoryHeroImages[category.slug] || '';

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
          { '@type': 'ListItem', position: 2, name: category.name, item: `${siteInfo.url}/category/${category.slug}` },
        ],
      },
      {
        '@type': 'CollectionPage',
        '@id': `${siteInfo.url}/category/${category.slug}`,
        name: `${category.name} - 萝幼社`,
        description: category.description,
        url: `${siteInfo.url}/category/${category.slug}`,
        isPartOf: { '@id': `${siteInfo.url}/#website` },
      },
    ],
  };

  return (
    <>
      <title>{category.name} - 萝幼社 | 官方正版授权二次元内容平台</title>
      <meta name="description" content={category.description} />
      <meta name="keywords" content={`${category.name},二次元${category.name},萝幼社${category.name},正版${category.name}内容`} />
      <link rel="canonical" href={`${siteInfo.url}/category/${category.slug}`} />
      <meta property="og:title" content={`${category.name} - 萝幼社`} />
      <meta property="og:description" content={category.description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`${siteInfo.url}/category/${category.slug}`} />
      <script type="application/ld+json">
        {JSON.stringify(schemaData)}
      </script>

      <main>
        <div className="pt-16">
          <Breadcrumb />

          <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${heroImage})` }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/40"></div>
            </div>
            <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm mb-4">
                <i className={`${category.icon} text-2xl text-white`}></i>
              </div>
              <h1 className="font-heading text-3xl md:text-4xl text-white">{category.name}</h1>
              <p className="mt-3 text-sm md:text-base text-white/70 max-w-lg text-center">
                {category.description}
              </p>
              <p className="mt-2 text-xs text-white/50">
                {category.articleCount.toLocaleString()} 篇作品
              </p>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto">
              {articles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {articles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      className="group rounded-lg overflow-hidden bg-background-100 hover:bg-background-200/50 transition-all duration-300 cursor-pointer"
                    >
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={article.imageUrl}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-2 text-xs text-foreground-500 mb-2">
                          <span>{article.author}</span>
                          <span>·</span>
                          <span>{article.publishDate}</span>
                        </div>
                        <h3 className="font-heading text-base text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center gap-3 mt-4 text-xs text-foreground-500">
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {article.viewCount.toLocaleString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-heart-line"></i>
                            {article.likeCount.toLocaleString()}
                          </span>
                          <span>{article.readTime}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                    <i className="ri-inbox-line text-2xl"></i>
                  </div>
                  <p className="mt-4 text-foreground-600">该分类暂无内容</p>
                </div>
              )}
            </div>
          </section>
        </div>
      </main>
    </>
  );
}